import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Chip,
  Divider,
  Card,
  CardContent,
  LinearProgress,
  Stack
} from '@mui/material';
import {
  Warning,
  PlayCircleOutline,
  Block,
  AutoMode,
  Schedule,
  Settings
} from '@mui/icons-material';

interface ConcurrencyLimitDialogProps {
  open: boolean;
  onClose: () => void;
  onAttach: () => void;
  maxActiveRuns: number;
  activeRunId: string;
  mode?: 'reuse' | 'reject' | 'allow';
}

const ConcurrencyLimitDialog: React.FC<ConcurrencyLimitDialogProps> = ({
  open,
  onClose,
  onAttach,
  maxActiveRuns,
  activeRunId,
  mode = 'reuse'
}) => {
  
  const getModeInfo = () => {
    switch (mode) {
      case 'reject':
        return {
          icon: <Block color="error" />,
          color: 'error' as const,
          titleKey: 'concurrencyLimit.rejectMode.title',
          descKey: 'concurrencyLimit.rejectMode.description'
        };
      case 'allow':
        return {
          icon: <AutoMode color="warning" />,
          color: 'warning' as const,
          titleKey: 'concurrencyLimit.allowMode.title',
          descKey: 'concurrencyLimit.allowMode.description'
        };
      default: // reuse
        return {
          icon: <PlayCircleOutline color="info" />,
          color: 'info' as const,
          titleKey: 'concurrencyLimit.reuseMode.title',
          descKey: 'concurrencyLimit.reuseMode.description'
        };
    }
  };

  const modeInfo = getModeInfo();

  return (
    <Dialog
      open={open}
      onClose={() => {}} // 禁用点击外部关闭
      disableEscapeKeyDown // 禁用ESC键关闭
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Warning sx={{ fontSize: 32, color: 'warning.main' }} />
          <Box>
            <Typography variant="h5" component="div" fontWeight="bold">
              🚦 并发限制 | Concurrency Limit
            </Typography>
            <Typography variant="body2" color="text.secondary">
              已达到最大并发运行数 | Maximum concurrent runs reached
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={3}>
          {/* 当前状态卡片 */}
          <Card sx={{ background: 'rgba(255, 255, 255, 0.9)', borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  📊 当前状态 | Current Status
                </Typography>
                <Chip 
                  label={`${maxActiveRuns}/1 运行中 | Running`}
                  color="error"
                  variant="filled"
                />
              </Box>
              
              <LinearProgress 
                variant="determinate" 
                value={100} 
                color="error"
                sx={{ height: 8, borderRadius: 4, mb: 2 }}
              />
              
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>🎯 活跃测试 ID | Active Test ID:</strong> <code>{activeRunId}</code>
                </Typography>
              </Alert>
              
              <Typography variant="body2" color="text.secondary">
                系统当前已达到最大并发运行数限制。为确保测试稳定性，同时只能运行 {maxActiveRuns} 个测试。
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                The system has reached the maximum concurrent run limit. Only {maxActiveRuns} test(s) can run simultaneously to ensure stability.
              </Typography>
            </CardContent>
          </Card>

          {/* 处理模式说明 */}
          <Card sx={{ background: 'rgba(255, 255, 255, 0.9)', borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                {modeInfo.icon}
                <Typography variant="h6" color="primary" fontWeight="bold">
                  ⚙️ 处理模式 | Processing Mode
                </Typography>
                <Chip 
                  label={mode.toUpperCase()}
                  color={modeInfo.color}
                  size="small"
                />
              </Box>
              
              <Alert severity={modeInfo.color} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  🔄 自动附着到现有测试 | Auto-attach to existing test
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  系统将自动连接您到当前运行的测试，您可以查看实时进度和日志。
                </Typography>
                <Typography variant="body2">
                  The system will automatically connect you to the currently running test where you can view real-time progress and logs.
                </Typography>
              </Alert>
            </CardContent>
          </Card>

          {/* 操作提示 */}
          <Card sx={{ background: 'rgba(255, 255, 255, 0.9)', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" color="primary" fontWeight="bold" mb={2}>
                💡 接下来 | Next Steps
              </Typography>
              
              <Stack spacing={2}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Schedule color="primary" />
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      等待当前测试完成 | Wait for current test to complete
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      或者附着到现有测试查看进度 | Or attach to existing test to view progress
                    </Typography>
                  </Box>
                </Box>
                
                <Divider />
                
                <Box display="flex" alignItems="center" gap={2}>
                  <Settings color="primary" />
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      调整并发设置 | Adjust concurrency settings
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      管理员可在配置中修改最大并发数 | Admins can modify max concurrent runs in configuration
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onAttach}
          variant="contained"
          color="primary"
          startIcon={<PlayCircleOutline />}
          fullWidth
          size="large"
          sx={{
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 'bold'
          }}
        >
          🔗 附着到测试 | Attach to Test
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConcurrencyLimitDialog;
